import { getContext } from '@fiverr-private/fiverr_context';
import type { Impression } from '@fiverr-private/bianka_js';
import { InspireDeliveryCard } from '@fiverr-private/bianka_js/protobuf/bianka/event/activity/v2/extension/inspire_delivery_card';
import { InspireDeliveryCarousel } from '@fiverr-private/bianka_js/protobuf/bianka/event/activity/v2/extension/inspire_delivery_carousel';
import { bigQuery, bianka } from '@fiverr-private/obs';
import { type ImpressionArgs } from '@fiverr-private/impressionable';
import type { InspireDelivery } from '../../../types/inspire';

export type Variant = 'carousel' | 'waterfall';
export type CardElement = 'primary_link' | 'seller_link' | 'gig_link' | 'favorite_button';

const BIANKA_TYPE = 'impression';
const BIANKA_SUB_GROUP = 'inspiration';

export interface BigQueryEvents {
    trackImpressionOnContainer: (props: { impressionArgs: ImpressionArgs }) => void;
    trackImpressionOnCard: (props: {
        inspireDelivery: InspireDelivery;
        index: number;
        impressionArgs: ImpressionArgs;
    }) => void;
    trackClickOnCard: (props: {
        inspireDelivery: InspireDelivery;
        index: number;
        impressionId: string;
        containerImpressionId: string;
        cardElement: CardElement;
    }) => void;
}

export interface BigQueryCommonProps {
    enabled: boolean;
    variant: Variant;
    pageName: string;
    containerPositionInPage: number;
}

export const createEvents = ({
    enabled,
    variant,
    containerPositionInPage,
    pageName,
}: BigQueryCommonProps): BigQueryEvents => ({
    trackImpressionOnContainer: ({ impressionArgs }) => {
        if (!enabled) return;

        const { pageCtxId } = getContext();

        bianka.reportActivity<Impression, InspireDeliveryCarousel>({
            type: BIANKA_TYPE,
            event: {
                type: `deliveries_${variant}_impression`,
                component: {
                    name: `deliveries_${variant}`,
                    positionInParent: containerPositionInPage,
                    impId: impressionArgs.imp_id,
                    level: impressionArgs.level,
                    isLeaf: impressionArgs.is_leaf,
                    sizeInParent: impressionArgs.size_in_parent,
                    visibleThroughScroll: impressionArgs.visible_through_scroll,
                },
            },
            additionalData: {
                type: InspireDeliveryCarousel,
                message: {
                    subGroup: BIANKA_SUB_GROUP,
                    page: {
                        name: pageName,
                        ctxId: pageCtxId,
                    },
                },
            },
        });
    },
    trackImpressionOnCard: ({ inspireDelivery, index, impressionArgs }) => {
        if (!enabled) return;

        const { pageCtxId } = getContext();

        bianka.reportActivity<Impression, InspireDeliveryCard>({
            type: BIANKA_TYPE,
            event: {
                type: `delivery_card_impression`,
                component: {
                    name: `delivery_card`,
                    positionInParent: index + 1,
                    impId: impressionArgs.imp_id,
                    parentComponentImpId: impressionArgs.parent_component_imp_id,
                    level: impressionArgs.level,
                    isLeaf: impressionArgs.is_leaf,
                    sizeInParent: impressionArgs.size_in_parent,
                    visibleThroughScroll: impressionArgs.visible_through_scroll,
                },
            },
            additionalData: {
                type: InspireDeliveryCard,
                message: {
                    subGroup: BIANKA_SUB_GROUP,
                    page: {
                        name: pageName,
                        ctxId: pageCtxId,
                    },
                    gig: {
                        id: Number(inspireDelivery.gig.id),
                    },
                    seller: {
                        id: Number(inspireDelivery.seller.id),
                    },
                    category: {
                        id: inspireDelivery.category.categoryId,
                    },
                    subCategory: {
                        id: inspireDelivery.category.subCategoryId,
                    },
                    ...(inspireDelivery.category.nestedCategoryId && {
                        nestedSubCategory: {
                            id: inspireDelivery.category.nestedCategoryId,
                        },
                    }),
                    inspireDelivery: {
                        id: inspireDelivery.id,
                        type: inspireDelivery.originalDeliveryAttachment.type,
                    },
                },
            },
        });
    },
    trackClickOnCard: ({ inspireDelivery, index, impressionId, containerImpressionId, cardElement }) => {
        if (!enabled) return;

        send({
            pageName,
            type: `clicked_on_delivery_card`,
            group: 'user_actions',
            actionType: 'click',
            impression: {
                id: impressionId,
                parent_component_id: containerImpressionId,
            },
            pageComponent: {
                name: 'delivery_card',
                ...(variant === 'carousel' ? { position_in_parent: index + 1 } : {}),
            },
            pageElement: getCardPageElement({ cardElement }),
            eventSource: 'delivery_card',
            inspireDelivery,
        });
    },
});

const getCardPageElement = ({ cardElement }: { cardElement: CardElement }): PageElement | undefined => {
    switch (cardElement) {
        case 'primary_link':
            return { type: 'link', name: 'delivery_page_link' };
        case 'seller_link':
            return { type: 'link', name: 'seller_title', text: 'about the seller' };
        case 'gig_link':
            return { type: 'link', name: 'gig_link', text: 'see_gig' };
        case 'favorite_button':
            return { type: 'button', name: 'add_to_list', text: 'favorite' };
        default:
            return undefined;
    }
};

interface PageElement {
    type: 'link' | 'button';
    name: string;
    text?: string;
}

interface PageComponent {
    name: string;
    position_in_parent?: number;
}

const send = ({
    pageName,
    type,
    group,
    actionType,
    impression,
    pageComponent,
    pageElement,
    eventSource,
    inspireDelivery,
}: {
    pageName: string;
    type: string;
    group: 'user_actions';
    actionType: 'click';
    impression?: {
        id: string;
        parent_component_id?: string;
    };
    pageComponent?: PageComponent;
    pageElement?: PageElement;
    eventSource?: string;
    inspireDelivery?: InspireDelivery;
}): void => {
    const { pageCtxId } = getContext();

    bigQuery.send({
        type,
        group,
        sub_group: 'inspiration',
        action: { type: actionType },
        page: {
            name: pageName,
            ctx_id: pageCtxId,
            ...(pageComponent ? { component: pageComponent } : {}),
            ...(pageElement ? { element: pageElement } : {}),
        },
        ...(impression ? { imp: impression } : {}),
        ...(eventSource ? { event_source: eventSource } : {}),
        ...(inspireDelivery
            ? {
                  seller: { id: inspireDelivery.seller.id },
                  gig: { id: inspireDelivery.gig.id },
                  category: { id: inspireDelivery.category.categoryId },
                  sub_category: { id: inspireDelivery.category.subCategoryId },
                  nested_sub_category: { id: inspireDelivery.category.nestedCategoryId },
                  inspire_delivery: { id: inspireDelivery.id, type: inspireDelivery.originalDeliveryAttachment.type },
              }
            : {}),
    });
};
